import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { CrmPipelineDeal } from '@bcc/shared';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { CrmToolsDialog } from '../../components/crm/CrmToolsDialog';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';

export function CrmPipelineDeals() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [deals, setDeals] = useState<CrmPipelineDeal[]>([]);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [toolsOpen, setToolsOpen] = useState(false);
  const [draftUw, setDraftUw] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const r = await apiFetch<{ deals: CrmPipelineDeal[] }>('/api/crm/pipeline-deals', { token });
      setDeals(r.deals);
    })();
  }, [token]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!token || !name.trim()) return;
    const r = await apiFetch<{ deal: CrmPipelineDeal }>('/api/crm/pipeline-deals', {
      method: 'POST',
      token,
      body: JSON.stringify({
        name: name.trim(),
        notes: notes.trim() || null,
        underwriting: Object.keys(draftUw).length ? draftUw : undefined,
      }),
    });
    setDeals((d) => [r.deal, ...d]);
    setName('');
    setNotes('');
    setDraftUw({});
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Pipeline deals</h1>
          <p className="mt-2 max-w-xl text-sm text-[var(--home-muted-on-dark)]">
            Underwriting workspace — scenarios save to each deal. Use tools to run calculators, then create or open a
            deal to attach numbers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setToolsOpen(true)}
          className="shrink-0 rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105"
        >
          Open underwriting tools
        </button>
      </div>

      <CrmToolsDialog
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        initialMerged={draftUw}
        onApply={(uw) => setDraftUw(uw)}
      />

      <form
        onSubmit={(e) => void create(e)}
        className={`max-w-xl space-y-4 p-6 ${SITE_GLASS_CARD}`}
      >
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">New pipeline deal</h2>
        {Object.keys(draftUw).length > 0 && (
          <p className="text-xs text-emerald-400">Calculator outputs will be saved with this deal.</p>
        )}
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
        </label>
        <button
          type="submit"
          className="rounded-xl border border-white/[0.12] bg-white/[0.08] px-6 py-2 text-sm font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/[0.12]"
        >
          Save deal
        </button>
      </form>

      <section className={`overflow-hidden ${SITE_GLASS_CARD}`}>
        <header className="border-b border-white/[0.08] px-5 py-3 text-sm font-semibold text-[var(--home-text-on-dark)]">
          Deal list
        </header>
        <ul className="divide-y divide-white/[0.06]">
          {deals.length === 0 ? (
            <li className="px-5 py-8 text-sm text-[var(--home-muted-on-dark)]">No pipeline deals yet.</li>
          ) : (
            deals.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div>
                  <Link
                    to={`/admin/crm/deals/${d.id}`}
                    className="font-medium text-[var(--home-text-on-dark)] hover:text-[var(--home-miami-blue)]"
                  >
                    {d.name}
                  </Link>
                  <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
                    Updated {new Date(d.updated_at).toLocaleString()}
                    {d.snapshots.length > 0 ? ` · ${d.snapshots.length} snapshots` : ''}
                  </p>
                </div>
                <Link
                  to={`/admin/crm/deals/${d.id}`}
                  className="rounded-full border border-white/[0.1] px-3 py-1.5 text-xs text-[var(--home-miami-blue)] transition hover:bg-white/[0.06]"
                >
                  Underwriting details →
                </Link>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}
